import {
    cancelSelectEvent,
    getCSSStyles,
    getEChartsTooltipTheme,
    getHeaderPinButtonHTML,
    getHeaderPinStateClass,
    getNoDataHTML,
    getWidgetDataURI,
    normalizeWidgetTheme,
    setupHeaderPin,
    toggleWidgetTheme
} from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface SectorBubbleItem {
    sector: string;
    risk: number;
    size: number;
    bubbleSize: number;
}

export class TraderverseSectorAnalysisConcentrationvsDiversificationRisk {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private chartData: SectorBubbleItem[] = [];
    private currentChart: any = null;
    private widgetResizeObserver: ResizeObserver | null = null;
    private resizeHandler: (() => void) | null = null;

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        return {
            headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
   <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Sector Analysis: Concentration vs Diversification Risk</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="chartDv" id="sectorSizeVsDiversificationRisk">
                <div class="insiderVolumeScale">
                    <div class="insiderVolumeScaleTicks" id="sectorRiskScaleTicks"></div>
                    <div class="insiderVolumeScaleBar"></div>
                </div>
                <div id="chartContainer" class="chart insiderVolumeChart"></div>
            </div>
          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        setupHeaderPin(this.shadowRoot, this.config);
    }

    private getDummyData(): SectorBubbleItem[] {
        return [
            { sector: 'Technology', risk: 18, size: 128, bubbleSize: 90 },
            { sector: 'Healthcare', risk: 22, size: 118, bubbleSize: 72 },
            { sector: 'Financials', risk: 24, size: 112, bubbleSize: 68 },
            { sector: 'Consumer Discretionary', risk: 16, size: 108, bubbleSize: 55 },
            { sector: 'Industrials', risk: 28, size: 122, bubbleSize: 60 },
            { sector: 'Energy', risk: 30, size: 148, bubbleSize: 95 },
            { sector: 'Materials', risk: 33, size: 98, bubbleSize: 48 },
            { sector: 'Utilities', risk: 36, size: 88, bubbleSize: 42 },
            { sector: 'Real Estate', risk: 42, size: 72, bubbleSize: 38 },
            { sector: 'Consumer Staples', risk: 20, size: 95, bubbleSize: 50 },
            { sector: 'Communication Services', risk: 17, size: 60, bubbleSize: 45 },
            { sector: 'Information Services', risk: 70, size: 35, bubbleSize: 88 }
        ];
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
            this.injectScaleLegendStyles();
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(): Promise<void> {
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const containerWrapper = this.shadowRoot.querySelector('#sectorSizeVsDiversificationRisk') as HTMLElement;
        if (widgetBox) widgetBox.classList.add('loading');

        this.chartData = this.getDummyData();
        containerWrapper?.setAttribute('data-testing-id', 'sector-size-vs-diversification-risk');
        this.updateDisplay();
        this.emit('dataUpdate', { data: this.chartData });

        if (widgetBox) widgetBox.classList.remove('loading');
    }

    private updateDisplay(): void {
        if (!(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.drawChart();
    }

    private formatBillions(value: number): string {
        if (value === 0) return '0';
        const abs = Math.abs(value);
        if (abs >= 1000) {
            const trillions = value / 1000;
            return Number.isInteger(trillions) ? `${trillions}T` : `${trillions.toFixed(1)}T`;
        }
        return Number.isInteger(value) ? `${value}B` : `${value.toFixed(1)}B`;
    }

    private getGradientColors(): string[] {
        return ['#33D17A', '#7AC74F', '#C5D63A', '#E8C41A', '#F0A01A', '#E86A1A', '#D9402A', '#B81E1E'];
    }

    private interpolateColor(ratio: number): string {
        const colors = this.getGradientColors();
        const clamped = Math.min(Math.max(ratio, 0), 1);
        const scaled = clamped * (colors.length - 1);
        const index = Math.floor(scaled);
        return colors[Math.min(index, colors.length - 1)];
    }

    private renderRiskScaleLegend(maxRisk: number): void {
        const ticksEl = this.shadowRoot.querySelector('#sectorRiskScaleTicks') as HTMLElement | null;
        if (!ticksEl) return;

        const tickCount = 8;
        const labels = Array.from({ length: tickCount + 1 }, (_, index) => {
            const value = (maxRisk / tickCount) * index;
            return Number.isInteger(value) ? String(value) : value.toFixed(0);
        });

        ticksEl.innerHTML = labels.map((label) => `<span>${label}</span>`).join('');
    }

    private updateWidgetLayout(chartHeight: number): void {
        const scaleLegendHeight = 48;
        const chartDv = this.shadowRoot.querySelector('#sectorSizeVsDiversificationRisk') as HTMLElement | null;
        const inBody = this.shadowRoot.querySelector('.inBody') as HTMLElement | null;
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;

        const chartDvHeight = scaleLegendHeight + chartHeight;
        if (chartDv) {
            chartDv.style.height = `${chartDvHeight}px`;
            chartDv.style.minHeight = `${chartDvHeight}px`;
            chartDv.style.flex = '0 0 auto';
        }

        if (this.config.autosize === false) return;

        const headTop = this.shadowRoot.querySelector('.headTop') as HTMLElement | null;
        const headTopHeight = headTop?.offsetHeight ?? 56;
        const inBodyPadding = 30;
        const inBodyHeight = chartDvHeight + inBodyPadding;

        if (inBody) {
            inBody.style.height = `${inBodyHeight}px`;
            inBody.style.minHeight = `${inBodyHeight}px`;
        }
        if (widgetBox) {
            widgetBox.style.height = `${headTopHeight + inBodyHeight}px`;
        }
    }

    private injectScaleLegendStyles(): void {
        const existing = this.shadowRoot.querySelector('style[data-sector-size-risk-scale]');
        if (existing) existing.remove();

        const style = document.createElement('style');
        style.setAttribute('data-sector-size-risk-scale', 'true');
        style.textContent = `
            #sectorSizeVsDiversificationRisk.chartDv {
                height: auto;
                min-height: 400px;
                display: flex;
                flex-direction: column;
                flex: 0 0 auto;
                overflow: visible;
            }
            .insiderVolumeScale {
                width: 100%;
                max-width: 420px;
                padding: 4px 0 12px;
                box-sizing: border-box;
                flex: 0 0 auto;
            }
            .insiderVolumeScaleBar {
                width: 100%;
                height: 12px;
                border-radius: 6px;
                background: linear-gradient(90deg, #33D17A 0%, #7AC74F 14%, #C5D63A 28%, #E8C41A 42%, #F0A01A 57%, #E86A1A 71%, #D9402A 85%, #B81E1E 100%);
            }
            .insiderVolumeScaleTicks {
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
                margin-bottom: 6px;
                color: var(--charts-text-color, #9A95AC);
                font-size: var(--font-size-sm, 12px);
                line-height: 1;
            }
            .insiderVolumeScaleTicks span {
                flex: 1;
                text-align: center;
            }
            .insiderVolumeScaleTicks span:first-child,
            .insiderVolumeScaleTicks span:last-child {
                flex: 0 0 auto;
            }
            .insiderVolumeScaleTicks span:first-child {
                text-align: left;
            }
            .insiderVolumeScaleTicks span:last-child {
                text-align: right;
            }
            #sectorSizeVsDiversificationRisk .insiderVolumeChart {
                position: relative !important;
                left: auto !important;
                top: auto !important;
                width: 100%;
                height: 400px;
                flex: 0 0 auto;
                min-height: 400px;
            }
            .widgetBox:has(#sectorSizeVsDiversificationRisk) .inBody {
                height: auto;
                min-height: 480px;
                overflow: visible;
            }
        `;
        this.shadowRoot.appendChild(style);
    }

    private formatSectorTooltip(sector: string): string {
        const parts = sector.trim().split(/\s+/);
        if (parts.length <= 1) return this.escapeHtml(sector);
        const mid = Math.ceil(parts.length / 2);
        return `${this.escapeHtml(parts.slice(0, mid).join(' '))}<br/>${this.escapeHtml(parts.slice(mid).join(' '))}`;
    }

    private escapeHtml(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
        const containerWrapper = this.shadowRoot.querySelector('#sectorSizeVsDiversificationRisk') as HTMLElement;

        if (!container) return;

        if (this.currentChart) {
            try { this.currentChart.dispose(); } catch { /* ignore */ }
            this.currentChart = null;
        }
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeHandler = null;
        }

        const chartData = [...this.chartData];
        if (!chartData.length) {
            container.innerHTML = getNoDataHTML();
            containerWrapper?.removeAttribute('data-testing-id');
            return;
        }

        container.innerHTML = '';
        containerWrapper?.setAttribute('data-testing-id', 'sector-size-vs-diversification-risk');

        const scaleMaxRisk = 80;
        const scaleMaxSize = 175;
        this.renderRiskScaleLegend(scaleMaxRisk);

        const bubbleValues = chartData.map((item) => item.bubbleSize);
        const minBubble = Math.min(...bubbleValues);
        const maxBubble = Math.max(...bubbleValues);

        const chartHeight = 400;
        container.style.height = `${chartHeight}px`;
        container.style.minHeight = `${chartHeight}px`;
        container.style.width = '100%';
        this.updateWidgetLayout(chartHeight);

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const widgetStyles = getComputedStyle(widgetBox);
        const textColor = widgetStyles.getPropertyValue('--charts-text-color').trim() || '#B8B3C9';
        const splitLineColor = widgetStyles.getPropertyValue('--charts-split-line-color').trim() || 'rgba(148, 163, 184, 0.25)';
        const fontSize = widgetStyles.getPropertyValue('--font-size-sm').trim() || '12px';
        const labelFontSize = parseInt(fontSize, 10) || 12;

        const midRisk = scaleMaxRisk / 2;
        const midSize = scaleMaxSize / 2;

        const scatterData = chartData.map((item) => {
            const riskRatio = scaleMaxRisk > 0 ? item.risk / scaleMaxRisk : 0;
            const sizeRatio = maxBubble === minBubble
                ? 0.5
                : (item.bubbleSize - minBubble) / (maxBubble - minBubble);
            const symbolSize = 28 + sizeRatio * 52;
            return {
                value: [item.risk, item.size, item.bubbleSize],
                sector: item.sector,
                symbolSize,
                itemStyle: {
                    color: this.interpolateColor(riskRatio),
                    opacity: 0.88
                }
            };
        });

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'item',
                confine: true,
                appendToBody: false,
                position: (point: number[]) => [point[0] - 40, point[1] + 18],
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (params: any) => {
                    const sector = params?.data?.sector ?? '';
                    return `
                    <div style="font-size:${fontSize}; line-height:1.35; text-align:center; min-width:90px;">
                        ${this.formatSectorTooltip(String(sector))}
                    </div>
                `;
                }
            },
            grid: { left: 16, right: 24, top: 16, bottom: 28, containLabel: true },
            xAxis: {
                type: 'value',
                min: 0,
                max: scaleMaxRisk,
                splitNumber: 8,
                axisLabel: {
                    color: textColor,
                    fontSize: labelFontSize,
                    margin: 10
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: splitLineColor,
                        type: 'dotted'
                    }
                },
                axisLine: { show: false },
                axisTick: { show: false }
            },
            yAxis: {
                type: 'value',
                min: 0,
                max: scaleMaxSize,
                splitNumber: 7,
                axisLabel: {
                    color: textColor,
                    fontSize: labelFontSize,
                    margin: 10,
                    formatter: (value: number) => this.formatBillions(value)
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: splitLineColor,
                        type: 'dotted'
                    }
                },
                axisLine: { show: false },
                axisTick: { show: false }
            },
            series: [
                {
                    type: 'scatter',
                    data: scatterData,
                    emphasis: {
                        scale: 1.08,
                        itemStyle: {
                            borderColor: '#fff',
                            borderWidth: 2,
                            shadowBlur: 8,
                            shadowColor: 'rgba(0,0,0,0.35)'
                        }
                    },
                    markLine: {
                        silent: true,
                        symbol: 'none',
                        animation: false,
                        label: { show: false },
                        data: [
                            {
                                xAxis: midRisk,
                                lineStyle: {
                                    color: '#E8C41A',
                                    type: 'dashed',
                                    width: 2,
                                    opacity: 0.95
                                }
                            },
                            {
                                yAxis: midSize,
                                lineStyle: {
                                    color: '#E05A4F',
                                    type: 'dashed',
                                    width: 2,
                                    opacity: 0.95
                                }
                            }
                        ]
                    }
                }
            ],
            animation: true,
            animationDuration: 900,
            animationEasing: 'cubicOut'
        };

        const chart = echarts.init(container, null, { renderer: 'canvas' });
        chart.setOption(option, true);
        chart.resize({ width: container.clientWidth || undefined, height: chartHeight });
        this.currentChart = chart;

        requestAnimationFrame(() => {
            chart.resize({ width: container.clientWidth || undefined, height: chartHeight });
        });

        this.resizeHandler = () => {
            chart.resize({ width: container.clientWidth || undefined, height: chartHeight });
            this.updateWidgetLayout(chartHeight);
        };
        window.addEventListener('resize', this.resizeHandler);
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }

    public destroy(): void {
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeHandler = null;
        }
        if (this.currentChart) {
            try { this.currentChart.dispose(); } catch { /* ignore */ }
            this.currentChart = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach(h => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global {
    interface Window {
        TraderverseSectorAnalysisConcentrationvsDiversificationRisk: typeof TraderverseSectorAnalysisConcentrationvsDiversificationRisk;
    }
}
if (typeof window !== 'undefined') {
    window.TraderverseSectorAnalysisConcentrationvsDiversificationRisk = TraderverseSectorAnalysisConcentrationvsDiversificationRisk;
}
